<template>
    <div class="box-header">
        <h3>
            {{ modelValue.title }}
            <span class="pull-right-indented">
                <span v-if="showModified"
                      class="modified-pill">
                    Modified
                </span>
                <button v-if="editable"
                        type="button"
                        class="btn portal-btn outline icon-button"
                        @click="editClicked">
                    <i class="icon icon-pencil" />
                </button>
            </span>
        </h3>
    </div>
    <div class="box-body">
        <view-edit-block v-model="relationshipBlock.relationshipLines"
                         :comparison="history"
                         list-type="ordered"
                         list-class="heavy-marker"
                         :layout="calcLayout()"
                         :inline-display="true"
                         :edit-mode-on="editModeOn"
                         :sending="sending"
                         :show-delete-item="true"
                         :show-add-item="true"
                         :table-on-print="true"
                         :printing="flat" 
                         @cancel-pressed="cancelPressed"
                         @update:model-value="updatePressed" />
    </div>
</template>

<script setup>
    import { defineProps, ref, computed, onMounted, defineEmits } from 'vue';
    import axios from 'axios';
    import { axiosConfig, axiosError } from '../../js/AjaxUtilities.js'
    import { canEdit } from '../../js/CalcGoalPlanEditState.js';
    import ViewEditBlock from '../viewEdit/ViewEditBlock.vue';

    const props = defineProps({
        modelValue: {
            type: Object,
            default: null
        },
        patientId: {
            type: Number,
            default: 0
        },
        editState: {
            type: Number,
            default: 0
        },
        flat: {
            type: Boolean,
            default: false
        },
        urlHead: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['update:modelValue']);

    const editPermission = computed(() => props.modelValue.canBeEditedByPatient ? 2 : 1);
    const editable = computed(() => canEdit(props.editState, editPermission.value));
    const showModified = computed(() => relationshipBlock.value.modified && props.editState == 1);

    const relationshipBlock = ref({ relationshipLines: [] });
    const editModeOn = ref(false);
    const sending = ref(false);

    const history = ref(null);

    onMounted(() => {
        relationshipBlock.value = generateRelationshipBlock();
    })

    function generateRelationshipBlock() {
        let relationshipBlock = { ...props.modelValue };
        relationshipBlock.relationshipLines = [];
        for (let i = 0; i < props.modelValue.relationshipLines.length; ++i) {
            let line = props.modelValue.relationshipLines[i];
            relationshipBlock.relationshipLines.push({ ...line });
        }
        return relationshipBlock;
    }

    function calcLayout() {
        return {
            id: `relatioship-block-${props.modelValue.goalPlanRelationshipBlockId}`,
            editTitle: `Update ${props.modelValue.title}`,
            editSize: "dialog-adaptive",
            inlineDisplay: true,
            rows: [
                {
                    columnClass: "adaptive-4-column",  // 3 columns + delete
                    columns: [
                        {
                            title: "Name",
                            editType: "text",
                            attributeName: "name"
                        },
                        {
                            title: "Relationship",
                            editType: "text",
                            attributeName: "relationship"
                        },
                        {
                            title: "Contact Details",
                            editType: "text",
                            attributeName: "contactDetails"
                        }
                    ]
                },
            ]
        };
    }

    function editClicked() {
        editModeOn.value = true;
        loadHistory();
    }

    function cancelPressed() {
        editModeOn.value = false;
    }

    function updatePressed() {
        sending.value = true;

        let data = {
            patientId: props.patientId,
            relationshipBlock: relationshipBlock.value
        };

        axios
            .post(`/${props.urlHead}/UpdateRelationshipBlock`, data, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    relationshipBlock.value = response.data.relationshipBlock;
                    emit("update:modelValue", relationshipBlock.value);
                } else {
                    alert(`Failed to update relationships: ${response.data.message}`);
                    relationshipBlock.value = generateRelationshipBlock(); // reset the edits back to how they were when passed in
                }
                sending.value = false;
            })
            .catch(function (error) {
                axiosError("Update relationship block", axios, error);
            })
            .finally(function () {
                editModeOn.value = false;
            });
    }

    function loadHistory() {
        if (!showModified.value) {
            history.value = null;
        } else if (!history.value) {
            let data = new Object();
            data.patientId = props.patientId;
            data.id = relationshipBlock.value.goalPlanRelationshipBlockId;


            axios
                .post(`/GoalPlan/GetPreviousRelationshipBlock`, data, axiosConfig())
                .then(function (response) {
                    if (response.data.success) {
                        history.value = response.data.relationshipBlock.relationshipLines;
                    } else {
                        alert(`Failed to load plan history: ${response.data.message}`);
                    }
                })
                .catch(function (error) {
                    axiosError("Get previous relationship block", axios, error);
                })
        }

    }


</script>